//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { ethers, Signer } from 'ethers'
// import fs from 'fs'
import { logOT } from './loggingShared'

export const ONE_DAY = 3600 * 24
export enum TIME_OF_DAY {
  BEFORE_CLOSE_OF_BUSINESS = 0,
  AFTER_CLOSE_OF_BUSINESS = 1,
}

export type FlexTestState = {
  //IPoolOverviewStateFlexStructOutpu
  poolAddr: string
  interestRate: bigint
  dailyInterestRate: bigint
  totalPrincipalEarningInterest: bigint
  totalInterestAccrued: bigint
  totalAssetsDeposited: bigint
  totalAssetsWithdrawn: bigint
  exchangeRate: bigint
  totalSupply: bigint
  totalRequestedShares: bigint
  totalRequestedAssets: bigint
  totalAssetsTransitioningIn: bigint
  totalSharesTransitioningIn: bigint
  totalAssetsDueForWithdraws: bigint
  exchangeRateCheckDiff: bigint
  lastDayAccrued: bigint

  totalFees: number
  feesOutstanding: number

  dailyOriginationFeeRate: number
  originationFee: number
  closeOfDepositTime: number
  closeOfWithdrawTime: number

  transferInDays: number
  transferOutDays: number

  // IPoolAccountingsFlexStructOutput
  accountAddr: string
  tokenBalance: bigint
  maxWithdrawRequest: bigint
  maxRedeemRequest: bigint
  requestedSharesOf: bigint
  requestedAssetsOf: bigint
  principalEarningInterest: bigint
  interestAccrued: bigint
  assetsDeposited: bigint
  assetsWithdrawn: bigint
  sharesTransitioningIn: bigint
  assetsTransitioningIn: bigint
  assetsDueForWithdraws: bigint
  sharesDueForWithdraws: bigint
  nonBusinessDays: bigint[]
} //IPoolOverviewStateFlexStructOutput & IPoolAccountingsFlexStructOutput

export interface BaseFixture {
  provider: ethers.JsonRpcProvider
  startTime: number
  tracePoolStates: TestcasePoolState[]
  prevState: TestcasePoolState
  nextState: TestcasePoolState

  prevStateFlex: FlexTestState
  nextStateFlex: FlexTestState

  currentDay: number
  contractSettings: ContractSettings

  poolAddr: string
  poolControllerAddr: string
  poolAddrs: string[]
  poolControllerAddrs: Record<string, string>

  tosAcceptanceRegistryAddr: string
  poolRegistryAddr: string
  businessDayRegistryAddr: string
  vaultFactoryAddr: string
  poolControllerFactoryAddr: string
  poolFactoryAddr: string
  loanFactoryAddr: string
  serviceConfigurationAddr: string
  queryLibAddr: string
  poolLibAddr: string
  loanLibAddr: string
  withdrawControllerAddr: string

  withdrawControllerFactoryAddr: string
  toSAcceptanceRegistryAddr: string
  poolAccessControlFactoryAddr: string
  poolLibFlexAddr: string
  poolFactoryFlexAddr: string
  withdrawDepositControllerFactoryFlexAddr: string
  poolControllerFactoryFlexAddr: string
  businessDayRegistryFactoryAddr: string

  adminSigner: Signer
  pauserSigner: Signer
  poolAdminSigner: Signer
  borrowerManagerSigner: Signer
  operatorSigner: Signer
  deployerSigner: Signer
  lenderASigner: Signer
  lenderBSigner: Signer
  lenderCSigner: Signer
  feeCollectorSigner: Signer
  borrowerWalletSigner: Signer
  poolAdminWalletSigner: Signer

  operatorAddr: string
  deployerAddr: string
  lenderAAddr: string
  lenderBAddr: string
  lenderCAddr: string
  mockUSDCAddr: string
  mockEurcAddr: string
  poolAdminAddr: string
  borrowerManagerAddr: string
  feeCollectorAddr: string
  borrowerWalletAddr: string
  poolAdminWalletAddr: string

  borrowerVaultAddr: string
  feeVaultAddr: string
  fundingVaultAddr: string
  currentLoanAddr: string
  priorLoanAddr: string
}

export interface CycleInput {
  LENDER_A_DEPOSIT: number
  LENDER_B_DEPOSIT: number
  LENDER_C_DEPOSIT: number
  LENDER_A_WITHDRAW_PERCENTAGE: number
  LENDER_B_WITHDRAW_PERCENTAGE: number
  LENDER_C_WITHDRAW_PERCENTAGE: number
  CHANGED_APR?: number
  LENDER_A_EARLY_WITHDRAW_PERCENTAGE: number
  LENDER_B_EARLY_WITHDRAW_PERCENTAGE: number
  LENDER_C_EARLY_WITHDRAW_PERCENTAGE: number
}

export interface CycleData {
  LENDER_A_DEPOSIT: number
  LENDER_B_DEPOSIT: number
  LENDER_C_DEPOSIT: number
  LENDER_A_WITHDRAW_PERCENTAGE: number
  LENDER_B_WITHDRAW_PERCENTAGE: number
  LENDER_C_WITHDRAW_PERCENTAGE: number
  LENDER_A_EARLY_WITHDRAW_PERCENTAGE: number
  LENDER_B_EARLY_WITHDRAW_PERCENTAGE: number
  LENDER_C_EARLY_WITHDRAW_PERCENTAGE: number
  CHANGED_APR?: number
  FINAL_APR: number
  INDICATIVE_APR: number

  LOAN_CREATE_DAY: number // -3
  DEPOSIT_DAY: number // -2
  INIT_ROLLOVER_DAY: number // -2
  ACCRUAL_START_DAY: number //  0 +7 from startTime
  REPAY_RELEASE_REDEEM_DAY: number // +2
  REDEMPTION_REQUEST_FOR_NEXT_CYCLE_EXECUTION_DAY: number
  REDEMPTION_REQUEST_CLOSING_DAY: number // -3 (do at -3)

  ACCRUAL_START_DAY_TIMESTAMP: number
  ACCRUAL_START_TIMESTAMP: number // +9
  DEPOSIT_CLOSING_TIMESTAMP: number // +9
  MATURING_TIMESTAMP: number // +9
  REDEMPTION_REQUEST_CLOSING_TIMESTAMP: number // +9
  REDEMPTION_AVAILABLE_TIMESTAMP: number
  NEXT_CYCLE_REDEMPTION_AVAILABLE_TIMESTAMP: number

  /// NOT NEEDED

  PRINCIPAL: number
  ROLLOVER_AMOUNT: number

  INDICATIVE_INTEREST: number
  FINAL_INTEREST: number

  PREVIOUS_CYCLE_FEES: number
  FEES: number
  FEE_COLLECTOR_BALANCE: number
  ENDING_VALUE: number
  TOTAL_PPT: number

  TOTAL_DEPOSITS: number
  TOTAL_WITHDRAW: number
  PRIOR_TOTAL_WITHDRAW: number

  OUTSTANDING_LOAN_PRINCIPALS_AFTER_REPAY: number
  OUTSTANDING_LOAN_PRINCIPALS: number
  ASSETS_TO_ROLLOVER_TO_NEXT_LOAN: number
  ASSETS_TO_RETURN_TO_POOL: number
  ASSETS_FROM_POOL: number
  ASSETS_TO_KEEP_IN_POOL: number

  LENDER_A_PRINCIPAL: number
  LENDER_A_PPT: number

  LENDER_A_END_VALUE: number
  LENDER_A_WITHDRAW: number
  LENDER_A_REDEEM: number
  LENDER_A_WITHDRAW_PRIOR: number
  LENDER_A_REDEEM_PRIOR: number

  LENDER_B_PRINCIPAL: number
  LENDER_B_PPT: number
  LENDER_B_REDEEM: number
  LENDER_B_WITHDRAW_PRIOR: number
  LENDER_B_REDEEM_PRIOR: number

  LENDER_B_END_VALUE: number
  LENDER_B_WITHDRAW: number

  LENDER_C_PRINCIPAL: number
  LENDER_C_PPT: number

  LENDER_C_END_VALUE: number
  LENDER_C_WITHDRAW: number
  LENDER_C_REDEEM: number
  LENDER_C_WITHDRAW_PRIOR: number
  LENDER_C_REDEEM_PRIOR: number

  TOTAL_REDEEM_PRIOR: number
  TOTAL_WITHDRAW_PRIOR: number

  EXCHANGE_RATE_AT_MATURITY: number
  EXCHANGE_RATE: number

  NET_ROLLOVER: number
  PRIOR_NET_ROLLOVER: number

  TOTAL_ASSETS_AT_DEPOSIT: number
  TOTAL_ASSETS_AT_ROLLOVER: number
  TOTAL_SUPPLY_AT_DEPOSIT: number
  TOTAL_ASSETS_DEPOSITED: number
  TOTAL_ASSETS_AT_REPAY: number
  LIQUIDITY_POOL_ASSETS_AFTER_DEPOSIT: number
  BORROWER_WALLET_BALANCE_PRIOR: number
  BORROWER_WALLET_BALANCE_DRAWDOWN: number
  BORROWER_WALLET_BALANCE_REPAY: number

  requestRedeemA: number
  requestWithdrawA: number

  requestRedeemB: number
  requestWithdrawB: number

  requestRedeemC: number
  requestWithdrawC: number

  exchangeRateAtDeposit: number
  exchangeRateAtMaturity: number
}

export const cycleDataSetup = (cycleData: CycleData, priorCycleData?: CycleData): void => {
  if (priorCycleData) {
    cycleData.exchangeRateAtDeposit = priorCycleData.exchangeRateAtMaturity
  } else {
    cycleData.exchangeRateAtDeposit = 1e18
  }
}
export interface TestcasePoolState {
  functionName: string
  cycleNumber: number
  currentDay: number
  poolAddr: string
  settledLoans: string[]
  state: number
  totalAvailableAssets: number
  totalAvailableSupply: number
  currentExpectedInterest: number
  liquidityPoolAssets: number
  totalAssets: number
  totalOutstandingLoanPrincipal: number
  totalAssetsDeposited: number
  totalAssetsWithdrawn: number
  totalRequestedShares: number
  totalRedeemableShares: number
  totalWithdrawableAssets: number
  totalRequestedAssets: number
  feeVaultBalance: number
  borrowerVaultBalance: number
  borrowerWalletBalance: number
  poolBalance: number
  exchangeRateAtMaturity: number

  requestedLoanState_loanAddr: string
  requestedLoanState_borrowerAddr: string
  requestedLoanState_fundingVaultAddr: string
  requestedLoanState_poolAddr: string
  requestedLoanState_state: number
  requestedLoanState_durationDays: number
  requestedLoanState_principal: number
  requestedLoanState_startingPrincipal: number
  requestedLoanState_interest: number
  requestedLoanState_indicativeInterest: number
  requestedLoanState_originationFee: number
  requestedLoanState_indicativeApr: number
  requestedLoanState_finalizedApr: number
  requestedLoanState_exchangeRateAtDeposit: number
  requestedLoanState_exchangeRateAtMaturity: number
  requestedLoanState_fundingVaultBalance: number
  requestedLoanState_assetsRolloverToNextLoan: number
  requestedLoanState_assetsToReturnToPool: number
  requestedLoanState_assetsFromPool: number
  requestedLoanState_accrualStartDayTimestamp: number
  requestedLoanState_accrualStartTimestamp: number
  requestedLoanState_transferInWindowDurationDays: number
  requestedLoanState_transferOutWindowDurationDays: number
  requestedLoanState_depositClosingTimestamp: number
  requestedLoanState_redemptionRequestClosingTimestamp: number
  requestedLoanState_maturingTimestamp: number
  requestedLoanState_redemptionAvailableTimestamp: number
  requestedLoanState_canRequestRedemption: boolean

  activeLoanState_loanAddr: string
  activeLoanState_borrowerAddr: string
  activeLoanState_fundingVaultAddr: string
  activeLoanState_poolAddr: string
  activeLoanState_state: number
  activeLoanState_durationDays: number
  activeLoanState_principal: number
  activeLoanState_startingPrincipal: number
  activeLoanState_interest: number
  activeLoanState_indicativeInterest: number
  activeLoanState_originationFee: number
  activeLoanState_indicativeApr: number
  activeLoanState_finalizedApr: number
  activeLoanState_exchangeRateAtDeposit: number
  activeLoanState_exchangeRateAtMaturity: number
  activeLoanState_fundingVaultBalance: number
  activeLoanState_assetsRolloverToNextLoan: number
  activeLoanState_assetsToReturnToPool: number
  activeLoanState_assetsFromPool: number
  activeLoanState_accrualStartDayTimestamp: number
  activeLoanState_accrualStartTimestamp: number
  activeLoanState_transferInWindowDurationDays: number
  activeLoanState_transferOutWindowDurationDays: number
  activeLoanState_depositClosingTimestamp: number
  activeLoanState_redemptionRequestClosingTimestamp: number
  activeLoanState_maturingTimestamp: number
  activeLoanState_redemptionAvailableTimestamp: number
  activeLoanState_canRequestRedemption: boolean

  maturedLoanState_loanAddr: string
  maturedLoanState_borrowerAddr: string
  maturedLoanState_fundingVaultAddr: string
  maturedLoanState_poolAddr: string
  maturedLoanState_state: number
  maturedLoanState_durationDays: number
  maturedLoanState_principal: number
  maturedLoanState_startingPrincipal: number
  maturedLoanState_interest: number
  maturedLoanState_indicativeInterest: number
  maturedLoanState_originationFee: number
  maturedLoanState_indicativeApr: number
  maturedLoanState_finalizedApr: number
  maturedLoanState_exchangeRateAtDeposit: number
  maturedLoanState_exchangeRateAtMaturity: number
  maturedLoanState_fundingVaultBalance: number
  maturedLoanState_assetsRolloverToNextLoan: number
  maturedLoanState_assetsToReturnToPool: number
  maturedLoanState_assetsFromPool: number
  maturedLoanState_accrualStartDayTimestamp: number
  maturedLoanState_accrualStartTimestamp: number
  maturedLoanState_transferInWindowDurationDays: number
  maturedLoanState_transferOutWindowDurationDays: number
  maturedLoanState_depositClosingTimestamp: number
  maturedLoanState_redemptionRequestClosingTimestamp: number
  maturedLoanState_maturingTimestamp: number
  maturedLoanState_redemptionAvailableTimestamp: number
  maturedLoanState_canRequestRedemption: boolean

  createdLoanState_loanAddr: string
  createdLoanState_borrowerAddr: string
  createdLoanState_fundingVaultAddr: string
  createdLoanState_poolAddr: string
  createdLoanState_state: number
  createdLoanState_durationDays: number
  createdLoanState_principal: number
  createdLoanState_startingPrincipal: number
  createdLoanState_interest: number
  createdLoanState_indicativeInterest: number
  createdLoanState_originationFee: number
  createdLoanState_indicativeApr: number
  createdLoanState_finalizedApr: number
  createdLoanState_exchangeRateAtDeposit: number
  createdLoanState_exchangeRateAtMaturity: number
  createdLoanState_fundingVaultBalance: number
  createdLoanState_assetsRolloverToNextLoan: number
  createdLoanState_assetsToReturnToPool: number
  createdLoanState_assetsFromPool: number
  createdLoanState_accrualStartDayTimestamp: number
  createdLoanState_accrualStartTimestamp: number
  createdLoanState_transferInWindowDurationDays: number
  createdLoanState_transferOutWindowDurationDays: number
  createdLoanState_depositClosingTimestamp: number
  createdLoanState_redemptionRequestClosingTimestamp: number
  createdLoanState_maturingTimestamp: number
  createdLoanState_redemptionAvailableTimestamp: number
  createdLoanState_canRequestRedemption: boolean

  feeCollectorBalance: number
  borrowerBalance: number
  poolAdminBalance: number

  lenderABalance: number
  lenderBBalance: number
  lenderCBalance: number

  investorA_poolAddr: string
  investorA_accountAddr: string
  investorA_balance: number
  investorA_maxWithdrawRequest: number
  investorA_maxRedeemRequest: number
  investorA_maxWithdraw: number
  investorA_maxRedeem: number
  investorA_requestedSharesOf: number
  investorA_redeemableSharesOf: number
  investorA_requestedAssetsOf: number
  investorA_withdrawableAssetsOf: number
  investorA_earlyRequestedSharesOf: number
  investorA_earlyRequestedAssetsOf: number
  investorA_earlyAcceptedSharesOf: number
  investorA_earlyAcceptedAssetsOf: number

  investorB_poolAddr: string
  investorB_accountAddr: string
  investorB_balance: number
  investorB_maxWithdrawRequest: number
  investorB_maxRedeemRequest: number
  investorB_maxWithdraw: number
  investorB_maxRedeem: number
  investorB_requestedSharesOf: number
  investorB_redeemableSharesOf: number
  investorB_requestedAssetsOf: number
  investorB_withdrawableAssetsOf: number
  investorB_earlyRequestedSharesOf: number
  investorB_earlyRequestedAssetsOf: number
  investorB_earlyAcceptedSharesOf: number
  investorB_earlyAcceptedAssetsOf: number

  investorC_poolAddr: string
  investorC_accountAddr: string
  investorC_balance: number
  investorC_maxWithdrawRequest: number
  investorC_maxRedeemRequest: number
  investorC_maxWithdraw: number
  investorC_maxRedeem: number
  investorC_requestedSharesOf: number
  investorC_redeemableSharesOf: number
  investorC_requestedAssetsOf: number
  investorC_withdrawableAssetsOf: number
  investorC_earlyRequestedSharesOf: number
  investorC_earlyRequestedAssetsOf: number
  investorC_earlyAcceptedSharesOf: number
  investorC_earlyAcceptedAssetsOf: number

  totalEarlyRequestedShares: number
  totalEarlyRequestedAssets: number
  totalEarlyAcceptedShares: number
  totalEarlyAcceptedAssets: number

  rolloverState: number
  rolloverActor: number
  rolloverTimeToActionWindow: number
  rolloverTimeLeftInActionWindow: number
  rolloverTimePastActionWindow: number
}

export const LOGGING_FORMAT_STR_START = '\n\u{1b}[32m                                   Day %d ------%s start \u{1b}[0m'
export const LOGGING_FORMAT_STR_END = '\u{1b}[33m                                   Day %d ------%s END \u{1b}[0m'

export interface ContractSettings {
  lenderStartingUSDCBalance: number
  // LENDER_A_DEPOSITAmount: 500_000_000000,
  // lenderAFirstRequestPPT: 200_000_000000,
  // LENDER_B_DEPOSITAmount: 100_000_000000,
  // lenderBFirstRequestPPT: 100_000_000000,
  borrowerInitialUSDC: number

  pool: {
    closeOfBusinessTime: number
    maxCapacity: number
    endDate: number
    borrowerManager: string
    earlyWithdrawFeeBps: number
  }
  loanSettings: {
    principal: number
    startingPrincipal: number
    indicativeApr: number
    finalizedApr: number
    durationDays: number
    dropDeadTimestamp: number
    originationBps: number
    accrualStartDayTimestamp: number
    transferInWindowDurationDays: number
    transferOutWindowDurationDays: number
    priorLoan: string
  }
}

export const createCycleData = (loanCycles: CycleInput[], INPUTS: ContractSettings): CycleData[] => {
  const cycleData: CycleData[] = []

  loanCycles.forEach((cycle, cycleNumber) => {
    const result: Partial<CycleData> = { ...cycle }
    /* tslint:disable */
    /* eslint-disable */
    const priorResult = cycleNumber > 0 ? cycleData[cycleNumber - 1] : ({} as CycleData)

    result.FINAL_APR = cycle.CHANGED_APR ?? INPUTS.loanSettings.finalizedApr
    result.INDICATIVE_APR = INPUTS.loanSettings.indicativeApr

    const apr = cycle.CHANGED_APR ? cycle.CHANGED_APR / 10000 : INPUTS.loanSettings.indicativeApr / 10000

    result.TOTAL_DEPOSITS = cycle.LENDER_A_DEPOSIT + cycle.LENDER_B_DEPOSIT + cycle.LENDER_C_DEPOSIT

    result.EXCHANGE_RATE = cycleNumber === 0 ? 1 : priorResult.EXCHANGE_RATE_AT_MATURITY

    if (cycleNumber > 0) {
      result.PREVIOUS_CYCLE_FEES = priorResult.FEES
      result.FEE_COLLECTOR_BALANCE = priorResult.FEE_COLLECTOR_BALANCE + priorResult.FEES
    } else {
      result.PREVIOUS_CYCLE_FEES = 0
      result.FEE_COLLECTOR_BALANCE = 0
    }
    result.LENDER_A_PRINCIPAL =
      cycle.LENDER_A_DEPOSIT + (cycleNumber > 0 ? priorResult.LENDER_A_END_VALUE - priorResult.LENDER_A_WITHDRAW : 0)
    result.LENDER_B_PRINCIPAL =
      cycle.LENDER_B_DEPOSIT + (cycleNumber > 0 ? priorResult.LENDER_B_END_VALUE - priorResult.LENDER_B_WITHDRAW : 0)
    result.LENDER_C_PRINCIPAL =
      cycle.LENDER_C_DEPOSIT + (cycleNumber > 0 ? priorResult.LENDER_C_END_VALUE - priorResult.LENDER_C_WITHDRAW : 0)

    result.LENDER_A_PPT = Math.round(result.LENDER_A_PRINCIPAL / result.EXCHANGE_RATE)
    result.LENDER_B_PPT = Math.round(result.LENDER_B_PRINCIPAL / result.EXCHANGE_RATE)
    result.LENDER_C_PPT = Math.round(result.LENDER_C_PRINCIPAL / result.EXCHANGE_RATE)
    result.TOTAL_PPT = result.LENDER_A_PPT + result.LENDER_B_PPT + result.LENDER_C_PPT

    result.LENDER_A_END_VALUE =
      result.LENDER_A_PRINCIPAL + Math.floor((result.LENDER_A_PRINCIPAL * apr * INPUTS.loanSettings.durationDays) / 360)

    result.LENDER_B_END_VALUE =
      result.LENDER_B_PRINCIPAL + Math.floor((result.LENDER_B_PRINCIPAL * apr * INPUTS.loanSettings.durationDays) / 360)
    result.LENDER_C_END_VALUE =
      result.LENDER_C_PRINCIPAL + Math.floor((result.LENDER_C_PRINCIPAL * apr * INPUTS.loanSettings.durationDays) / 360)
    result.ENDING_VALUE = result.LENDER_A_END_VALUE + result.LENDER_B_END_VALUE + result.LENDER_C_END_VALUE

    result.EXCHANGE_RATE_AT_MATURITY = result.TOTAL_PPT == 0 ? 1 : result.ENDING_VALUE / result.TOTAL_PPT

    result.LENDER_A_EARLY_WITHDRAW_PERCENTAGE = cycle.LENDER_A_EARLY_WITHDRAW_PERCENTAGE
    result.LENDER_B_EARLY_WITHDRAW_PERCENTAGE = cycle.LENDER_B_EARLY_WITHDRAW_PERCENTAGE
    result.LENDER_C_EARLY_WITHDRAW_PERCENTAGE = cycle.LENDER_C_EARLY_WITHDRAW_PERCENTAGE

    result.LENDER_A_WITHDRAW = Math.floor(result.LENDER_A_END_VALUE * cycle.LENDER_A_WITHDRAW_PERCENTAGE)
    result.LENDER_B_WITHDRAW = Math.floor(result.LENDER_B_END_VALUE * cycle.LENDER_B_WITHDRAW_PERCENTAGE)
    result.LENDER_C_WITHDRAW = Math.floor(result.LENDER_C_END_VALUE * cycle.LENDER_C_WITHDRAW_PERCENTAGE)
    result.LENDER_A_REDEEM = Math.floor(result.LENDER_A_PPT * cycle.LENDER_A_WITHDRAW_PERCENTAGE)
    result.LENDER_B_REDEEM = Math.floor(result.LENDER_B_PPT * cycle.LENDER_B_WITHDRAW_PERCENTAGE)
    result.LENDER_C_REDEEM = Math.floor(result.LENDER_C_PPT * cycle.LENDER_C_WITHDRAW_PERCENTAGE)

    result.LENDER_A_WITHDRAW_PRIOR = cycleNumber === 0 ? 0 : priorResult.LENDER_A_WITHDRAW
    result.LENDER_B_WITHDRAW_PRIOR = cycleNumber === 0 ? 0 : priorResult.LENDER_B_WITHDRAW
    result.LENDER_C_WITHDRAW_PRIOR = cycleNumber === 0 ? 0 : priorResult.LENDER_C_WITHDRAW

    result.LENDER_A_REDEEM_PRIOR = cycleNumber === 0 ? 0 : priorResult.LENDER_A_REDEEM
    result.LENDER_B_REDEEM_PRIOR = cycleNumber === 0 ? 0 : priorResult.LENDER_B_REDEEM
    result.LENDER_C_REDEEM_PRIOR = cycleNumber === 0 ? 0 : priorResult.LENDER_C_REDEEM

    result.TOTAL_REDEEM_PRIOR =
      result.LENDER_A_REDEEM_PRIOR + result.LENDER_B_REDEEM_PRIOR + result.LENDER_C_REDEEM_PRIOR
    result.TOTAL_WITHDRAW_PRIOR =
      result.LENDER_A_WITHDRAW_PRIOR + result.LENDER_B_WITHDRAW_PRIOR + result.LENDER_C_WITHDRAW_PRIOR
    result.TOTAL_WITHDRAW = result.LENDER_A_WITHDRAW + result.LENDER_B_WITHDRAW + result.LENDER_C_WITHDRAW
    result.ASSETS_TO_ROLLOVER_TO_NEXT_LOAN = 0
    result.ASSETS_TO_RETURN_TO_POOL = 0
    result.ASSETS_FROM_POOL = result.TOTAL_DEPOSITS
    result.PRINCIPAL = result.TOTAL_DEPOSITS
    result.ASSETS_TO_KEEP_IN_POOL = 0
    result.OUTSTANDING_LOAN_PRINCIPALS = result.TOTAL_DEPOSITS
    if (cycleNumber > 0) {
      if (priorResult.TOTAL_WITHDRAW > result.TOTAL_DEPOSITS) {
        result.OUTSTANDING_LOAN_PRINCIPALS =
          priorResult.OUTSTANDING_LOAN_PRINCIPALS_AFTER_REPAY - result.ASSETS_TO_RETURN_TO_POOL
        result.ASSETS_TO_KEEP_IN_POOL = result.TOTAL_DEPOSITS
        result.ASSETS_TO_RETURN_TO_POOL = priorResult.TOTAL_WITHDRAW - result.TOTAL_DEPOSITS
        result.ASSETS_TO_ROLLOVER_TO_NEXT_LOAN = priorResult.ENDING_VALUE - result.ASSETS_TO_RETURN_TO_POOL
        result.ASSETS_FROM_POOL = 0
      } else {
        result.ASSETS_TO_KEEP_IN_POOL = priorResult.TOTAL_WITHDRAW
        result.ASSETS_FROM_POOL = result.TOTAL_DEPOSITS - priorResult.TOTAL_WITHDRAW

        result.OUTSTANDING_LOAN_PRINCIPALS =
          priorResult.OUTSTANDING_LOAN_PRINCIPALS_AFTER_REPAY + result.ASSETS_FROM_POOL
        result.ASSETS_TO_RETURN_TO_POOL = 0
        result.ASSETS_TO_ROLLOVER_TO_NEXT_LOAN = priorResult.ENDING_VALUE
      }
      result.PRINCIPAL = result.TOTAL_DEPOSITS + priorResult.ENDING_VALUE - priorResult.TOTAL_WITHDRAW
    }
    result.PRIOR_TOTAL_WITHDRAW = cycleNumber === 0 ? 0 : priorResult.TOTAL_WITHDRAW
    result.OUTSTANDING_LOAN_PRINCIPALS_AFTER_REPAY = result.PRINCIPAL
    result.FINAL_INTEREST = Math.floor(
      (result.PRINCIPAL * (result.FINAL_APR / 10000) * INPUTS.loanSettings.durationDays) / 360
    )
    result.INDICATIVE_INTEREST = Math.floor(
      (result.PRINCIPAL * (result.INDICATIVE_APR / 10000) * INPUTS.loanSettings.durationDays) / 360
    )
    result.TOTAL_ASSETS_AT_REPAY =
      result.OUTSTANDING_LOAN_PRINCIPALS_AFTER_REPAY + result.PRIOR_TOTAL_WITHDRAW + result.FINAL_INTEREST

    result.FEES = Math.floor(
      (result.PRINCIPAL * INPUTS.loanSettings.originationBps * INPUTS.loanSettings.durationDays) / 360 / 10000
    )

    result.ROLLOVER_AMOUNT = 0

    result.NET_ROLLOVER = result.TOTAL_DEPOSITS - result.TOTAL_WITHDRAW

    if (cycleNumber > 0) {
      result.LIQUIDITY_POOL_ASSETS_AFTER_DEPOSIT = priorResult.FINAL_INTEREST + result.TOTAL_DEPOSITS
    }
    result.TOTAL_ASSETS_AT_DEPOSIT =
      result.ASSETS_TO_ROLLOVER_TO_NEXT_LOAN + result.ASSETS_TO_RETURN_TO_POOL + result.TOTAL_DEPOSITS

    result.TOTAL_ASSETS_AT_ROLLOVER =
      result.OUTSTANDING_LOAN_PRINCIPALS + result.ASSETS_TO_KEEP_IN_POOL + result.FINAL_INTEREST
    if (cycleNumber === 0) {
      result.TOTAL_SUPPLY_AT_DEPOSIT = result.TOTAL_PPT
      result.TOTAL_ASSETS_DEPOSITED = result.TOTAL_DEPOSITS
      result.LIQUIDITY_POOL_ASSETS_AFTER_DEPOSIT = result.TOTAL_DEPOSITS
    } else {
      result.TOTAL_SUPPLY_AT_DEPOSIT = priorResult.TOTAL_PPT + Math.round(result.TOTAL_DEPOSITS / result.EXCHANGE_RATE)
      result.TOTAL_ASSETS_DEPOSITED = result.TOTAL_DEPOSITS + priorResult.TOTAL_ASSETS_DEPOSITED
    }

    if (cycleNumber === 0) {
      result.BORROWER_WALLET_BALANCE_PRIOR = 100000_000000
    } else {
      result.BORROWER_WALLET_BALANCE_PRIOR = priorResult.BORROWER_WALLET_BALANCE_REPAY
    }
    result.BORROWER_WALLET_BALANCE_REPAY =
      result.BORROWER_WALLET_BALANCE_PRIOR - result.ASSETS_TO_RETURN_TO_POOL + result.ASSETS_FROM_POOL
    result.BORROWER_WALLET_BALANCE_DRAWDOWN = result.BORROWER_WALLET_BALANCE_PRIOR + result.ASSETS_FROM_POOL

    cycleData.push(result as CycleData)
  })

  return cycleData
}

export const calculateDataTimeStamps = (CYCLE_DATA: CycleData[], INPUTS: ContractSettings, startTime: number) => {
  const startTimeDaysSince1970 = Math.floor((startTime + ONE_DAY) / ONE_DAY)
  const startTimeMidnightUTC = startTimeDaysSince1970 * ONE_DAY
  const startTimeAtCloseOfBusiness = startTimeMidnightUTC + INPUTS.pool.closeOfBusinessTime

  logOT.debug('closeOfBusinessTime', INPUTS.pool.closeOfBusinessTime)
  let priorCycle: CycleData | undefined = undefined
  CYCLE_DATA.forEach((cycle: CycleData) => {
    cycle.ACCRUAL_START_DAY = priorCycle ? priorCycle.ACCRUAL_START_DAY + INPUTS.loanSettings.durationDays : 7
    cycle.INIT_ROLLOVER_DAY = cycle.ACCRUAL_START_DAY - INPUTS.loanSettings.transferInWindowDurationDays
    cycle.LOAN_CREATE_DAY = cycle.INIT_ROLLOVER_DAY - 2

    cycle.DEPOSIT_DAY = cycle.INIT_ROLLOVER_DAY - 1

    cycle.REDEMPTION_REQUEST_CLOSING_DAY =
      cycle.ACCRUAL_START_DAY + INPUTS.loanSettings.durationDays - INPUTS.loanSettings.transferOutWindowDurationDays
    cycle.REPAY_RELEASE_REDEEM_DAY = cycle.ACCRUAL_START_DAY + INPUTS.loanSettings.transferOutWindowDurationDays
    cycle.REDEMPTION_REQUEST_FOR_NEXT_CYCLE_EXECUTION_DAY = cycle.REPAY_RELEASE_REDEEM_DAY + 2

    cycle.ACCRUAL_START_DAY_TIMESTAMP = startTimeMidnightUTC + cycle.ACCRUAL_START_DAY * ONE_DAY
    cycle.ACCRUAL_START_TIMESTAMP = startTimeAtCloseOfBusiness + cycle.ACCRUAL_START_DAY * ONE_DAY

    cycle.DEPOSIT_CLOSING_TIMESTAMP = startTimeAtCloseOfBusiness + cycle.INIT_ROLLOVER_DAY * ONE_DAY
    cycle.MATURING_TIMESTAMP =
      startTimeAtCloseOfBusiness + (cycle.ACCRUAL_START_DAY + INPUTS.loanSettings.durationDays) * ONE_DAY
    cycle.REDEMPTION_REQUEST_CLOSING_TIMESTAMP =
      startTimeAtCloseOfBusiness + cycle.REDEMPTION_REQUEST_CLOSING_DAY * ONE_DAY
    cycle.REDEMPTION_AVAILABLE_TIMESTAMP = startTimeAtCloseOfBusiness + cycle.REPAY_RELEASE_REDEEM_DAY * ONE_DAY
    cycle.NEXT_CYCLE_REDEMPTION_AVAILABLE_TIMESTAMP =
      startTimeAtCloseOfBusiness + (cycle.REPAY_RELEASE_REDEEM_DAY + INPUTS.loanSettings.durationDays) * ONE_DAY
    priorCycle = cycle
  })
}

export const poolLoanStatePretty = (loanState: Record<string, any> | undefined): Record<string, any> | undefined => {
  if (loanState) {
    const loanStatePretty: Record<string, any> = {}
    Object.keys(loanState).forEach((k: string) => {
      if (isNaN(parseInt(k)) && k !== 'accountings' && k != 'rolloverAccounting') {
        const value = loanState[k]
        if (typeof value === 'bigint' || typeof value === 'number') {
          loanStatePretty[k] = value.toString()
        } else {
          loanStatePretty[k] = value
        }
      }
    })

    if (loanState.feesAtMaturity) {
      const fam: Record<string, any> = {}
      Object.keys(loanState.feesAtMaturity).forEach((k: string) => {
        if (isNaN(parseInt(k))) {
          const value = (loanState.feesAtMaturity as Record<string, any>)[k]
          if (typeof value === 'bigint' || typeof value === 'number') {
            fam[k] = value.toString()
          } else {
            fam[k] = value
          }
        }
      })
      loanStatePretty.feesAtMaturity = fam
    }
    return loanStatePretty
  }
  return undefined
}

export const fromWei = (value: string | number): string => {
  return Math.floor(Number(value) / 1000000).toString()
}

export const poolStatePretty = (state: Record<string, any>): Record<string, any> => {
  const t: Record<string, any> = {}
  Object.keys(state).forEach((k: string) => {
    if (isNaN(parseInt(k)) && k !== 'accountings') {
      const value = state[k]
      if (k === 'requestedLoanState' || k === 'activeLoanState' || k === 'maturedLoanState') {
        t[k] = poolLoanStatePretty(value as Record<string, any>)
      } else if (typeof value === 'bigint') {
        t[k] = (value / BigInt(1e6)).toString()
      } else if (typeof value === 'number') {
        t[k] = Math.floor(value / 1000000).toString()
      } else if (
        typeof value === 'object' &&
        k !== 'requestedLoanState' &&
        k !== 'activeLoanState' &&
        k !== 'maturedLoanState'
      ) {
        t[k] = poolStatePretty(value)
      } else {
        t[k] = value
      }
    }
  })
  // t.requestedLoanState = poolLoanStatePretty(state.requestedLoanState)
  // t.activeLoanState = poolLoanStatePretty(state.activeLoanState)
  // t.maturedLoanState = poolLoanStatePretty(state.maturedLoanState)

  return t
}

export const checkAreNumbersDifferent = (
  a: number | string | boolean | string[],
  b: number | string | boolean | string[],
  leftoverWei?: boolean
): boolean => {
  try {
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) {
        return true
      }
      for (let i = 0; i < a.length; i++) {
        if (checkAreNumbersDifferent(a[i], b[i])) {
          return true
        }
      }
      return false
    }
    if (typeof a === 'string' && a.startsWith('0x')) {
      return a !== b
    }
    if (typeof b === 'boolean' && typeof a === 'boolean') {
      return a !== b
    }

    const aNum = Number(a)
    const bNum = Number(b)
    if (aNum > 10000000000000000) {
      return Math.round(aNum / 100000000) !== Math.round(bNum / 100000000)
    }

    if (aNum > 10000) {
      return Math.round(aNum / 100) !== Math.round(bNum / 100)
    }
    if (leftoverWei && aNum < 10 && bNum < 10) {
      return false
    }
    return aNum !== bNum
  } catch (e) {
    const aBigNum = BigInt(a as string | number | bigint)
    const bBigNum = BigInt(b as string | number | bigint)
    if (aBigNum > BigInt(10000)) {
      return aBigNum / BigInt(100) != bBigNum / BigInt(100)
    }
    return aBigNum != bBigNum
  }
}

export const numbersDiff = (
  a: number | string | boolean | string[],
  b: number | string | boolean | string[]
): number => {
  try {
    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) {
        return a.length - b.length
      }
      for (let i = 0; i < a.length; i++) {
        const diff = numbersDiff(a[i], b[i])
        if (diff !== 0) {
          return diff
        }
      }
      return 0
    }
    if (typeof a === 'string' && a.startsWith('0x')) {
      return 0
    }

    const aNum = Number(a)
    const bNum = Number(b)

    return aNum - bNum
  } catch (e) {
    if (Array.isArray(a) && Array.isArray(b)) {
      return 0
    }
    const aBigNum = BigInt(a as string | number | bigint)
    const bBigNum = BigInt(b as string | number | bigint)

    return Number(aBigNum - bBigNum)
  }
}

export const stateChangeCheck = (
  fixture: BaseFixture,
  expectedChanges: Partial<TestcasePoolState>
): string | undefined => {
  const nextState: TestcasePoolState = fixture.nextState
  const previousState: TestcasePoolState = fixture.prevState

  const GREEN = '\u{1b}[32m'
  const RED = '\u{1b}[31m'
  let changeString: string | undefined = undefined

  const nextRecordState = nextState as unknown as Record<string, string | number | boolean | string[]>
  const previousRecordState = previousState as unknown as Record<string, string | number | boolean | string[]>
  const expectedRecordState = expectedChanges as unknown as Record<string, string | number | boolean | string[]>

  Object.keys(nextState).forEach((key: string) => {
    if (isNaN(parseInt(key)) && key !== 'functionName' && key !== 'currentDay') {
      const nextValue = nextRecordState[key]
      const previousValue = previousRecordState[key]
      const expectedValue = expectedRecordState?.[key]
      const fullKey = key.padEnd(30)
      if (expectedValue !== undefined) {
        const leftoverWei =
          key == 'poolBalance' ||
          key == 'requestedLoanState_principal' ||
          key == 'requestedLoanState_startingPrincipal' ||
          key == 'activeLoanState_assetsRolloverToNextLoan'
        if (checkAreNumbersDifferent(nextValue, expectedValue, leftoverWei)) {
          const str = `\n${fullKey} changed from ${GREEN}${previousValue}${RED} to ${GREEN}${nextValue}${RED} expected ${GREEN}${expectedValue}${RED}  DifferenceOf ${numbersDiff(
            nextValue,
            previousValue
          )} expected DifferenceOf ${numbersDiff(expectedValue, previousValue)}`
          if (changeString) {
            changeString += str
          } else {
            changeString = str
          }
        }
      } else {
        if (checkAreNumbersDifferent(nextValue, previousValue)) {
          const str = `\n${fullKey} changed from ${GREEN}${previousValue}${RED} to ${GREEN}${nextValue}${RED} NoChangeExpected DifferenceOf ${numbersDiff(
            nextValue,
            previousValue
          )}`
          if (changeString) {
            changeString += str
          } else {
            changeString = str
          }
        }
      }
    }
  })
  if (changeString) {
    // @ts-ignore
    changeString += '\n'
  }
  return changeString
}

export const stateChangeCheckFlex = (
  fixture: BaseFixture,
  expectedChanges: Partial<FlexTestState>
): string | undefined => {
  const nextState: FlexTestState = fixture.nextStateFlex
  const previousState: FlexTestState = fixture.prevStateFlex

  const GREEN = '\u{1b}[32m'
  const RED = '\u{1b}[31m'
  let changeString: string | undefined = undefined

  const nextRecordState = nextState as unknown as Record<string, string | number | boolean | string[]>
  const previousRecordState = previousState as unknown as Record<string, string | number | boolean | string[]>
  const expectedRecordState = expectedChanges as unknown as Record<string, string | number | boolean | string[]>

  if (nextState && previousState) {
    Object.keys(nextState).forEach((key: string) => {
      if (
        isNaN(parseInt(key)) &&
        key !== 'functionName' &&
        key !== 'currentDay' &&
        key !== 'name' &&
        key !== 'symbol'
      ) {
        const nextValue = nextRecordState[key]
        const previousValue = previousRecordState[key]
        const expectedValue = expectedRecordState?.[key]
        const fullKey = key.padEnd(30)
        if (expectedValue !== undefined) {
          const leftoverWei = key == 'poolBalance'
          if (checkAreNumbersDifferent(nextValue, expectedValue, leftoverWei)) {
            const str = `\n${fullKey} changed from ${GREEN}${previousValue}${RED} to ${GREEN}${nextValue}${RED} expected ${GREEN}${expectedValue}${RED}  DifferenceOf ${numbersDiff(
              nextValue,
              previousValue
            )} expected DifferenceOf ${numbersDiff(expectedValue, previousValue)}`
            if (changeString) {
              changeString += str
            } else {
              changeString = str
            }
          }
        } else {
          if (checkAreNumbersDifferent(nextValue, previousValue)) {
            const str = `\n${fullKey} changed from ${GREEN}${previousValue}${RED} to ${GREEN}${nextValue}${RED} NoChangeExpected DifferenceOf ${numbersDiff(
              nextValue,
              previousValue
            )}`
            if (changeString) {
              changeString += str
            } else {
              changeString = str
            }
          }
        }
      }
    })
  }
  if (changeString) {
    // @ts-ignore
    changeString += '\n'
  }
  return changeString
}

export function flattenObject(obj: Record<string, any>, prefix = ''): Record<string, any> {
  const flattened: Record<string, any> = {}

  for (const key in obj) {
    if (!key.endsWith('Addr')) {
      if (typeof obj[key] === 'object' && obj[key] !== null) {
        const nestedKeys = flattenObject(obj[key], `${prefix}${key}.`)
        Object.assign(flattened, nestedKeys)
      } else {
        flattened[`${prefix}${key}`] = obj[key]
      }
    }
  }

  return flattened
}

// export function convertToCSV(data: TestcasePoolState[], fileName: string): void {
//   const csvRows = []

//   if (data.length === 0) {
//     return
//   }
//   const headers = Object.keys(data[0])
//   csvRows.push(headers.join(','))

//   // Extract the values from each object and create a CSV row
//   for (const poolState of data) {
//     const recordObject = poolState as unknown as Record<string, string | number | boolean>
//     const values = headers.map((header) => {
//       const value = recordObject[header]
//       // If the value contains a comma, wrap it in double quotes to preserve CSV format

//       if (value && typeof value === 'string' && value.includes(',')) {
//         return `"${value}"`
//       }
//       return value
//     })
//     csvRows.push(values.join(','))
//   }

//   // Join all CSV rows with a line break
//   const csvContent = csvRows.join('\n')

//   fs.writeFileSync(fileName, csvContent, 'utf-8')
// }

const toNumber = (value: any): number => {
  if (typeof value === 'bigint') {
    try {
      return Number(value)
    } catch (e) {
      return Number(value / BigInt(1e6))
    }
  } else if (typeof value === 'number') {
    return value
  } else if (typeof value === 'string') {
    return parseInt(value)
  }
  return 666
}
const numberType = (value: any): string => {
  if (typeof value === 'bigint') {
    return 'BigInt'
  } else if (typeof value === 'number') {
    return 'number'
  } else if (typeof value === 'string') {
    return 'string'
  }
  return typeof value
}

const getValue = (property: string, subProp: string | undefined, state: Record<string, any>): any => {
  try {
    // @ts-ignore
    return subProp && state[property] ? state[property][subProp] : state[property]
  } catch (e) {
    return 666
  }
}
export const diffOfProperty = (
  property: string,
  subProp: string | undefined,
  prevState: Record<string, any>,
  nextState: Record<string, any>,
  expectedState: Record<string, any>,
  cycleData: CycleData
): void => {
  const prevValue = getValue(property, subProp, prevState)
  const nextValue = getValue(property, subProp, nextState)
  const expectedValue = getValue(property, subProp, expectedState)
  const prevNumber: number = toNumber(prevValue)
  const nextNumber: number = toNumber(nextValue)
  const expectedNumber: number = toNumber(expectedValue)
  const prevType: string = numberType(prevValue)
  const nextType: string = numberType(nextValue)
  const expectedType: string = numberType(expectedValue)

  const change = nextNumber - prevNumber
  const cycleDataMatch: string[] = []
  const cd = cycleData as Record<string, any>
  Object.keys(cycleData).forEach((key: string) => {
    const cdValue = toNumber(cd[key])
    const cdType = numberType(cd[key])
    if (cdValue === change) {
      cycleDataMatch.push(`Add ${key} ${cdValue} ${cdType}`)
    }
    if (cdValue === -change) {
      cycleDataMatch.push(`Subtract ${key} ${cdValue} ${cdType}`)
    }
    if (nextNumber === cdValue) {
      cycleDataMatch.push(`Set ${key} ${cdValue} ${cdType}`)
    }
  })
  logOT.debug(
    'diffOfProperty',
    property,
    prevType,
    prevNumber,
    nextType,
    nextNumber,
    expectedType,
    expectedNumber,
    'Increased by',
    nextNumber - prevNumber
  )
  if (change == 0) {
    logOT.debug('      NO CHANGE')
  } else {
    cycleDataMatch.forEach((cdm: string) => {
      logOT.debug('      ', cdm)
    })
  }
}

export function weiRound(received: number | BigInt | string, digits: number = 2): number {
  const div = Math.pow(10, digits)
  let receivedNum = 0
  if (typeof received == 'string') {
    receivedNum = Math.floor(Number(received) / div)
  }
  if (typeof received == 'number') {
    receivedNum = Math.floor(received / div)
  }
  if (typeof received === 'bigint') {
    receivedNum = Number(received / BigInt(div))
  }

  return receivedNum
}

export const LOAN_STATE_ZERO = {
  loanAddr: '0x0000000000000000000000000000000000000000',
  borrowerAddr: '0x0000000000000000000000000000000000000000',
  fundingVaultAddr: '0x0000000000000000000000000000000000000000',
  poolAddr: '0x0000000000000000000000000000000000000000',
  state: '0',
  durationDays: '0',
  principal: '0',
  interest: '0',
  indicativeInterest: '0',
  originationFee: '0',
  indicativeApr: '0',
  finalizedApr: '0',
  exchangeRateAtDeposit: '0',
  exchangeRateAtMaturity: '0',
  fundingVaultBalance: '0',
  assetsRolloverToNextLoan: '0',
  assetsToReturnToPool: '0',
  accrualStartDayTimestamp: '0',
  accrualStartTimestamp: '0',
  transferInWindowDurationDays: '0',
  transferOutWindowDurationDays: '0',
  depositClosingTimestamp: '0',
  redemptionRequestClosingTimestamp: '0',
  maturingTimestamp: '0',
  redemptionAvailableTimestamp: '0',
  canRequestRedemption: false,
}

export function selectCSV(fixture: BaseFixture, functionNames: string[], fieldNames: string[]): void {
  const spacing = 35
  const csvRows = []

  const data = fixture.tracePoolStates.filter((poolState) => {
    return functionNames.includes(poolState.functionName)
  })

  if (data.length === 0) {
    return
  }

  csvRows.push(fieldNames.map((fieldName) => fieldName.padEnd(spacing)).join(','))

  // Extract the values from each object and create a CSV row
  for (const poolState of data) {
    const recordObject = poolState as unknown as Record<string, string | number | boolean>
    const values = fieldNames.map((fieldName) => {
      const value = recordObject[fieldName]
      return value.toString().padEnd(spacing)
    })
    csvRows.push(values.join(','))
  }

  // Join all CSV rows with a line break
  const csvContent = csvRows.join('\n')
  logOT.debug(csvContent)
}
export async function delay(time: number): Promise<void> {
  return await new Promise((resolve) => setTimeout(resolve, time))
}

export const advanceToDay = async (
  startTimeMidnightUTC: number,
  numberDays: number,
  timeOfDay: TIME_OF_DAY,
  fixture: BaseFixture
): Promise<Date> => {
  if (fixture.provider._network.chainId < 10) {
    logOT.debug('skip advanceToDay ')
    return new Date()
  }
  //  const blockNumberB = await fixture.provider.getBlockNumber()
  const timestampStart = (await fixture.provider.getBlock('latest'))?.timestamp ?? 0

  logOT.debug(
    'startTimeMidnightUTC',
    timeOfDay === TIME_OF_DAY.AFTER_CLOSE_OF_BUSINESS,
    timeOfDay,
    TIME_OF_DAY.AFTER_CLOSE_OF_BUSINESS
  )
  const targetTime =
    timeOfDay === TIME_OF_DAY.AFTER_CLOSE_OF_BUSINESS
      ? startTimeMidnightUTC + numberDays * ONE_DAY + fixture.contractSettings.pool.closeOfBusinessTime + 3600
      : startTimeMidnightUTC + numberDays * ONE_DAY + fixture.contractSettings.pool.closeOfBusinessTime - 3600

  logOT.debug('Hour In Day startTimeMidnightUTC', (startTimeMidnightUTC % ONE_DAY) / 3600)

  logOT.debug(
    'Hour In Day fixture.contractSettings.pool.closeOfBusinessTime - 3600',
    ((fixture.contractSettings.pool.closeOfBusinessTime - 3600) % ONE_DAY) / 3600
  )

  logOT.debug('Hour In Day targetTime', (targetTime % ONE_DAY) / 3600)

  logOT.debug('ADVACE TO DAY', Math.round(targetTime - timestampStart))

  if (targetTime > timestampStart) {
    await fixture.provider.send('evm_mine', [])
    await fixture.provider.send('evm_increaseTime', [Math.round(targetTime - timestampStart) + 5])
    await fixture.provider.send('evm_mine', [])
  }

  let timestampNow = 0
  const blockEnd = await fixture.provider.getBlock('latest')
  timestampNow = blockEnd?.timestamp ?? 0

  while (timestampNow < targetTime) {
    await fixture.provider.send('evm_mine', [])
    await delay(100)
    const blockEnd = await fixture.provider.getBlock('latest')
    logOT.debug('wait, targetTime- timestampNow', targetTime - timestampNow)
    timestampNow = blockEnd?.timestamp ?? 0
  }

  //  const blockNumber = await fixture.provider.getBlockNumber()
  const timestampEnd = (await fixture.provider.getBlock('latest'))?.timestamp ?? 0

  const newDate = new Date(timestampEnd * 1000)

  fixture.currentDay = numberDays
  return newDate
}
